class CartRecommendations extends HTMLElement {
  constructor() {
    super();
    this.swiperInstance = null;
    this.loading = false;
    this.lastCartSignature = '';
  }

  connectedCallback() {
    requestAnimationFrame(() => {
      this.initializeSwiper();
      this.load();
    });
  }

  get recommendationsBaseUrl() {
    return (
      this.dataset.recommendationsUrl ||
      (typeof window !== 'undefined' &&
        (window.routes?.product_recommendations_url ||
          window.Shopify?.routes?.product_recommendations_url)) ||
      '/recommendations/products'
    );
  }

  getCartSignature(cart) {
    return cart.items.map((item) => `${item.product_id}:${item.quantity}`).join('|');
  }

  /**
   * Split total slide limit across cart line products (fair share + remainder).
   * e.g. limit 8, 3 products → [3, 3, 2]; limit 8, 2 products → [4, 4]
   */
  splitRecommendationLimit(totalLimit, productCount) {
    if (productCount <= 0) return [];
    const base = Math.floor(totalLimit / productCount);
    const remainder = totalLimit % productCount;
    return Array.from({ length: productCount }, (_, i) => base + (i < remainder ? 1 : 0));
  }

  async load() {
    if (this.loading) return;
    this.loading = true;
    this.classList.add('cart-recommendations--loading');

    try {
      const cart = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
      }).then((r) => r.json());

      if (!cart?.items?.length) {
        this.clear();
        return;
      }

      const signature = this.getCartSignature(cart);

      if (
        signature === this.lastCartSignature &&
        this.querySelector('[data-cart-recommendations-content] .swiper-wrapper')
      ) {
        await this.updateVisibility(cart);
        return;
      }

      this.lastCartSignature = signature;

      if (!this.querySelector('.swiper-wrapper')) {
        return;
      }

      const totalLimit = parseInt(this.dataset.limit, 10) || 12;
      const { chain, apiCount, filledFromCollection } = await this.fetchRecommendationSlides(cart);

      if (chain.length > 0) {
        this.replaceSlides(chain);
        if (apiCount === 0) {
          this.dataset.recommendationSource = 'collection';
        } else if (filledFromCollection) {
          this.dataset.recommendationSource = 'mixed';
        } else {
          this.dataset.recommendationSource = 'search-discovery';
        }
      } else {
        // No API/collection slides from JS — keep Liquid fallback already in the DOM
        this.dataset.recommendationSource = 'collection';
      }

      await this.updateVisibility(cart);
    } catch (e) {
      console.error('[CartRecommendations]', e);
    } finally {
      this.loading = false;
      this.classList.remove('cart-recommendations--loading');
    }
  }

  clear() {
    this.lastCartSignature = '';
    delete this.dataset.recommendationSource;
    this.innerHTML = '';
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }
  }

  async fetchRecommendationSlides(cart) {
    const totalLimit = parseInt(this.dataset.limit, 10) || 12;
    const sectionId = this.dataset.sectionId || 'cart-drawer-recommendations';
    const intent = this.dataset.intent || 'complementary';
    const baseUrl = this.recommendationsBaseUrl;
    const cartProductIds = new Set(cart.items.map((item) => String(item.product_id)));
    const anchorIds = [...new Set(cart.items.map((item) => item.product_id))];
    const perProductLimits = this.splitRecommendationLimit(totalLimit, anchorIds.length);
    const seen = new Set();

    const buckets = await this.fetchRecommendationBuckets({
      anchorIds,
      perProductLimits,
      seen,
      cartProductIds,
      sectionId,
      intent,
      baseUrl,
    });

    await this.backfillRecommendationBuckets({
      anchorIds,
      buckets,
      perProductLimits,
      seen,
      cartProductIds,
      sectionId,
      intent,
      baseUrl,
      totalLimit,
    });

    let chain = this.buildRecommendationChain(buckets, totalLimit);
    const apiCount = chain.length;
    let filledFromCollection = false;

    if (chain.length < totalLimit) {
      const beforeFill = chain.length;
      chain = await this.fillChainFromCollection(chain, totalLimit, cartProductIds, sectionId);
      filledFromCollection = chain.length > beforeFill;
    }

    return { chain, apiCount, filledFromCollection };
  }

  /**
   * Pad the chain with theme fallback collection products until totalLimit (or collection runs out).
   */
  async fillChainFromCollection(chain, totalLimit, cartProductIds, sectionId) {
    const needed = totalLimit - chain.length;
    if (needed <= 0) return chain;

    const seen = new Set(cartProductIds);
    chain.forEach((slide) => {
      const id = slide.getAttribute('data-product-id');
      if (id) seen.add(id);
    });

    const fillers = [];

    // 1. Liquid already rendered the fallback collection inside this element — reuse it first
    this.querySelectorAll('[data-cart-recommendations-content] .swiper-slide[data-product-id]').forEach(
      (slide) => {
        if (fillers.length >= needed) return;

        const id = slide.getAttribute('data-product-id');
        if (!id || seen.has(id) || cartProductIds.has(id)) return;

        seen.add(id);
        fillers.push(slide.cloneNode(true));
      }
    );

    // 2. Section fetch if the SSR pool did not have enough unique products
    if (fillers.length < needed) {
      const text = await this.fetchCollectionSection(sectionId, totalLimit);
      const fetched = this.slidesFromResponse(text, needed - fillers.length, seen, cartProductIds);
      fillers.push(...fetched);
    }

    return [...chain, ...fillers].slice(0, totalLimit);
  }

  async fetchCollectionSection(sectionId, limit) {
    const root = window.Shopify?.routes?.root || '/';
    const base = root.endsWith('/') ? root.slice(0, -1) : root;
    const query = `source=collection&limit=${limit}`;
    const urls = [
      `${base}/?sections=${sectionId}&${query}`,
      `/cart?sections=${sectionId}&${query}`,
      `${base}/?section_id=${sectionId}&${query}`,
      `/cart?section_id=${sectionId}&${query}`,
      `${window.location.pathname}?section_id=${sectionId}&${query}`,
    ];

    for (const url of urls) {
      try {
        const response = await fetch(url, { headers: { Accept: 'application/json, text/html' } });
        if (!response.ok) continue;

        const text = await response.text();

        try {
          const json = JSON.parse(text);
          if (json[sectionId]) return json[sectionId];
        } catch (_) {
          /* HTML response */
        }

        if (this.parseRecommendationsContent(text)) return text;
      } catch (e) {
        console.warn('[CartRecommendations] collection section fetch failed:', url, e);
      }
    }

    return '';
  }

  async fetchRecommendationBuckets({
    anchorIds,
    perProductLimits,
    seen,
    cartProductIds,
    sectionId,
    intent,
    baseUrl,
  }) {
    const responses = await Promise.all(
      anchorIds.map((productId, index) =>
        this.fetchRecommendationResponse(
          productId,
          perProductLimits[index] || 1,
          sectionId,
          intent,
          baseUrl
        )
      )
    );

    return responses.map((text, index) =>
      this.slidesFromResponse(text, perProductLimits[index] || 0, seen, cartProductIds)
    );
  }

  /**
   * When a cart product has no recommendations, give its slot(s) to products that do.
   * e.g. A has 6 recs, B has 0, limit 8 → refetch A with up to 8 and show all 6 from A.
   */
  async backfillRecommendationBuckets({
    anchorIds,
    buckets,
    perProductLimits,
    seen,
    cartProductIds,
    sectionId,
    intent,
    baseUrl,
    totalLimit,
  }) {
    let unusedSlots = perProductLimits.reduce(
      (sum, limit, index) => sum + Math.max(0, limit - buckets[index].length),
      0
    );

    if (unusedSlots <= 0) return;

    const recipientIndices = buckets.map((bucket, index) => (bucket.length > 0 ? index : -1)).filter((i) => i >= 0);

    if (recipientIndices.length === 0) return;

    const bonusLimits = this.splitRecommendationLimit(unusedSlots, recipientIndices.length);

    for (let j = 0; j < recipientIndices.length; j++) {
      if (unusedSlots <= 0) break;

      const index = recipientIndices[j];
      const bonus = bonusLimits[j] || 0;
      if (bonus <= 0) continue;

      buckets[index].forEach((slide) => {
        const id = slide.getAttribute('data-product-id');
        if (id) seen.delete(id);
      });

      const newLimit = Math.min(totalLimit, buckets[index].length + bonus);
      const text = await this.fetchRecommendationResponse(
        anchorIds[index],
        newLimit,
        sectionId,
        intent,
        baseUrl
      );

      buckets[index] = this.slidesFromResponse(text, newLimit, seen, cartProductIds);
      unusedSlots = Math.max(0, unusedSlots - bonus);
    }
  }

  fetchRecommendationResponse(productId, limit, sectionId, intent, baseUrl) {
    return fetch(
      `${baseUrl}?section_id=${sectionId}&product_id=${productId}&limit=${limit}&intent=${intent}`,
      { headers: { Accept: 'text/html' } }
    ).then((r) => (r.ok ? r.text() : ''));
  }

  slidesFromResponse(text, maxSlides, seen, cartProductIds) {
    const bucket = [];
    if (!text || maxSlides <= 0) return bucket;

    const content = this.parseRecommendationsContent(text);
    if (!content) return bucket;

    content.querySelectorAll('.swiper-slide[data-product-id]').forEach((slide) => {
      if (bucket.length >= maxSlides) return;

      const id = slide.getAttribute('data-product-id');
      if (!id || seen.has(id) || cartProductIds.has(id)) return;

      seen.add(id);
      bucket.push(slide.cloneNode(true));
    });

    return bucket;
  }

  /**
   * Build A₁, B₁, C₁, A₂, B₂, C₂… using only buckets that have slides (B empty → no gap).
   * Return the first `totalLimit` slides from that chain for the swiper.
   */
  buildRecommendationChain(buckets, totalLimit) {
    const activeBuckets = buckets.filter((bucket) => bucket.length > 0);
    if (activeBuckets.length === 0) return [];

    const chain = [];
    let round = 0;

    while (chain.length < totalLimit) {
      let addedThisRound = false;

      for (let i = 0; i < activeBuckets.length; i++) {
        if (chain.length >= totalLimit) break;

        const slide = activeBuckets[i][round];
        if (slide) {
          chain.push(slide);
          addedThisRound = true;
        }
      }

      if (!addedThisRound) break;
      round += 1;
    }

    return chain.slice(0, totalLimit);
  }

  parseRecommendationsContent(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const sectionId = this.dataset.sectionId || 'cart-drawer-recommendations';

    return (
      doc.querySelector('[data-cart-recommendations-content]') ||
      doc.querySelector(`#shopify-section-${sectionId} [data-cart-recommendations-content]`) ||
      doc.querySelector('.shopify-section [data-cart-recommendations-content]')
    );
  }

  replaceSlides(slides) {
    const wrapper = this.querySelector('.cart-drawer-recommendations-swiper .swiper-wrapper');
    if (!wrapper || slides.length === 0) return;

    this.concealRecommendationsSwiper();

    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }

    wrapper.innerHTML = '';
    slides.forEach((slide) => wrapper.appendChild(slide));
    this.initializeSwiper();
  }

  concealRecommendationsSwiper() {
    const el = this.querySelector('.cart-drawer-recommendations-swiper');
    if (!el) return;
    el.classList.remove('is-ready');
    void el.offsetWidth;
  }

  revealRecommendationsSwiper() {
    const el = this.querySelector('.cart-drawer-recommendations-swiper');
    if (!el) return;
    requestAnimationFrame(() => {
      el.classList.add('is-ready');
    });
  }

  initializeSwiper() {
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }

    const swiperWrapper = this.querySelector('.cart-drawer-recommendations-swiper');
    if (!swiperWrapper) return;

    const swiperContainer = swiperWrapper.querySelector('.swiper');
    if (!swiperContainer || typeof Swiper === 'undefined') {
      if (typeof Swiper === 'undefined') {
        this._swiperRetryCount = (this._swiperRetryCount || 0) + 1;
        if (this._swiperRetryCount <= 25) {
          setTimeout(() => this.initializeSwiper(), 100);
        }
      }
      return;
    }
    this._swiperRetryCount = 0;

    const visibleSlides = swiperContainer.querySelectorAll('.swiper-slide:not(.is-in-cart)');
    if (visibleSlides.length === 0) return;

    this.swiperInstance = new Swiper(swiperContainer, {
      slidesPerView: 2,
      spaceBetween: 16,
      loop: false,
      watchOverflow: true,
      breakpoints: {
        750: {
          slidesPerView: 3,
          spaceBetween: 20,
        },
      },
      navigation: {
        nextEl: this.querySelector('.swiper-button-next'),
        prevEl: this.querySelector('.swiper-button-prev'),
      },
      pagination: {
        el: this.querySelector('.swiper-pagination'),
        clickable: true,
        type: 'bullets',
      },
    });
    this.revealRecommendationsSwiper();
  }

  async updateVisibility(cartFromCaller) {
    try {
      const cart =
        cartFromCaller ||
        (await fetch('/cart.js', {
          headers: { Accept: 'application/json' },
        }).then((r) => r.json()));

      if (!cart?.items?.length) {
        this.clear();
        return;
      }

      const cartProductIds = new Set(cart.items.map((item) => String(item.product_id)));
      const slides = this.querySelectorAll('.swiper-slide[data-product-id]');
      let hasChanges = false;

      slides.forEach((slide) => {
        const productId = slide.getAttribute('data-product-id');
        const isInCart = cartProductIds.has(productId);

        if (isInCart && !slide.classList.contains('is-in-cart')) {
          slide.classList.add('is-in-cart');
          slide.style.display = 'none';
          hasChanges = true;
        } else if (!isInCart && slide.classList.contains('is-in-cart')) {
          slide.classList.remove('is-in-cart');
          slide.style.display = '';
          hasChanges = true;
        }
      });

      if (hasChanges) {
        this.concealRecommendationsSwiper();
        if (this.swiperInstance) {
          this.swiperInstance.update();
        } else {
          this.initializeSwiper();
        }
        this.revealRecommendationsSwiper();
      }
    } catch (e) {
      console.error('[CartRecommendations]', e);
    }
  }
}

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}
